import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, Button, Divider, Skeleton, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Page } from "v2/components/Page";
import { formatSaoPauloDate, formatSaoPauloDateTime } from "./dates";
import { getMailStatus, getPanelHome, getRondaStatus } from "./operational-panel.service";
import { IFailureEvent, IPanelHomeCnpj, IReliabilityNote } from "./contracts/home.types";
import { ConduccoRow } from "./home/ConduccoRow";
import { HealthyRow } from "./home/HealthyRow";
import { MailSection } from "./home/MailSection";
import { IPanelFilterState, PanelFilter, matchesPanelSearch } from "./home/PanelFilter";
import { RondaSection } from "./home/RondaSection";
import { HOME_TOKENS } from "./home/tokens";
import { useContractStatus } from "./useContractStatus";

const QUERY_KEY = ["operational-panel", "home"];
const CINCO_MINUTOS = 5 * 60 * 1000;

/** Os três `state` que ESTA versão da tela sabe classificar (espelho de `CnpjState`). */
const ESTADOS_CONHECIDOS: readonly string[] = ["CONDUCCO", "EM_DIA", "PARADO"];

/**
 * Valor de filtro que NÃO é um `cnpj.state`: é status de contrato. Está no
 * mesmo select porque o critério é o mesmo do operador ("me mostra este
 * grupo") e porque, sem ele, "Todos" deixaria de ser a soma das opções — os
 * CNPJs em configuração saem dos três setores de `state` e ficariam sem
 * nenhuma opção que os alcance. Conta CNPJ, igual às outras três.
 */
const FILTRO_EM_CONFIGURACAO = "EM_CONFIGURACAO";

const VALORES_DE_FILTRO: readonly string[] = [...ESTADOS_CONHECIDOS, FILTRO_EM_CONFIGURACAO];

/**
 * Achado D, parte 1 (review final v2.1, 2026-08-20): o `status` da query
 * string entrava cru no filtro. Um link editado à mão (`?status=parado`,
 * minúsculo) não casa com nenhum `cnpj.state`, então os três setores ficavam
 * vazios e o select "Situação" ficava EM BRANCO — valor fora das opções do
 * MUI —, sem nada na tela dizendo qual filtro estava ativo. Valor inválido
 * cai no default (sem filtro), e o `useEffect` que sincroniza a URL apaga o
 * lixo de lá na sequência.
 */
function sanitizeStatus(raw: string | null): string {
  return raw && VALORES_DE_FILTRO.includes(raw) ? raw : "";
}

/**
 * Achado D, parte 2: os dois repos não fazem deploy atômico, então o backend
 * pode passar a emitir um `state` que esta tela não conhece. Ele não pode cair
 * em "Saudáveis" (falso verde, o pior defeito desta tela) nem em "Exige a
 * Conducco" (falso vermelho, que quebraria "PARADO não é erro" por analogia):
 * ganha zona própria, cinza, contada à parte no veredito. Assim o CNPJ nunca
 * desaparece silenciosamente da lista enquanto continua somando no total.
 */
function isEstadoConhecido(state: IPanelHomeCnpj["state"]): boolean {
  return ESTADOS_CONHECIDOS.includes(state);
}

/**
 * Tela inicial da v2.1 (T9) — reescrita completa do que era `PortfolioPage`
 * até 2026-08-18 (732 linhas, 4 filtros, "Ver todos", agregação por causa
 * atrás de expandir/recolher, `refetchInterval` de 5s).
 *
 * Medido em produção: de 16 CNPJs ativos, 2 tinham falha real, 3 estavam
 * parados, o resto ia bem — mas a tela antiga mostrava ~40 alertas
 * vermelhos para um punhado de situações de verdade. O backend (`getPanelHome`)
 * agora faz essa classificação (`IPanelHomeCnpj.state`); esta tela só
 * particiona o que já chega pronto, sem reordenar (`generatedAt` já vem
 * `CONDUCCO` mais recente primeiro, `EM_DIA` alfabético, `PARADO` mais
 * antigo primeiro).
 *
 * Sem polling: reordenar a lista debaixo do operador enquanto ele lia era a
 * queixa concreta. `staleTime` de 5 min + botão "atualizar" explícito.
 *
 * Filtro (busca + situação) vive na query string, não em `useState` solto —
 * é o defeito que fazia o operador "se perder": sair para um CNPJ e voltar
 * resetava o filtro.
 */
export function PortfolioPage() {
  const history = useHistory();
  const location = useLocation();

  const initialFilter = useMemo<IPanelFilterState>(() => {
    const params = new URLSearchParams(location.search);
    return { search: params.get("q") ?? "", status: sanitizeStatus(params.get("status")) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filter, setFilter] = useState<IPanelFilterState>(initialFilter);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter.search) params.set("q", filter.search);
    if (filter.status) params.set("status", filter.status);
    const nextSearch = params.toString();
    const nextLocationSearch = nextSearch ? `?${nextSearch}` : "";
    if (nextLocationSearch !== location.search) {
      history.replace({ pathname: location.pathname, search: nextLocationSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPanelHome,
    staleTime: CINCO_MINUTOS,
    refetchOnWindowFocus: false,
  });

  /**
   * Query própria, chave própria: a Ronda de Invariantes é uma fonte
   * separada de `getPanelHome` (não faz parte de `IPanelHome`), então uma
   * falha ou atraso nela nunca deve derrubar nem re-renderizar as zonas
   * CONDUCCO/EM_DIA/PARADO acima.
   *
   * `rondaIsError` é repassado: enquanto carrega, `RondaSection` não desenha
   * nada; quando a leitura falha, ela alarma. Silêncio por falha de fonte é
   * exatamente o que a ronda existe para eliminar.
   */
  const { data: rondaStatus, isError: rondaIsError, refetch: refetchRonda } = useQuery({
    queryKey: ["operational-panel", "ronda"],
    queryFn: getRondaStatus,
    staleTime: CINCO_MINUTOS,
    refetchOnWindowFocus: false,
  });

  // why: query propria, como a Ronda: uma falha aqui nunca derruba as zonas do
  // painel, e uma falha do painel nunca esconde o sinal de e-mails.
  const { data: mailStatus, isError: mailIsError, refetch: refetchMail } = useQuery({
    queryKey: ["operational-panel", "mail"],
    queryFn: () => getMailStatus(),
    staleTime: CINCO_MINUTOS,
    refetchOnWindowFocus: false,
  });

  /**
   * Bloqueante (review final v2.1, 2026-08-20): até aqui era
   * `fonteIndisponivel = isError || data?.sourcesAvailable === false`, e o
   * ternário da renderização mostrava SÓ o `Alert`. Mas o React Query preserva
   * `data` quando a query erra (`status: "error"` não toca o cache): carga
   * inicial com 2 CNPJs CONDUCCO -> operador clica "atualizar" -> GET 502 ->
   * as duas falhas reais desapareciam da tela e sobrava um banner falando de
   * "o que aparece abaixo", com nada abaixo.
   *
   * Agora banner E lista, juntos. E os dois casos NÃO compartilham texto:
   *   - `isError` — não conseguimos falar com o servidor agora; o que está na
   *     tela é o dado de antes.
   *   - `sourcesAvailable === false` — o servidor respondeu, mas alguma fonte
   *     dele (Airflow/Postgres) não pôde ser lida; a lista está incompleta.
   * Podem valer ao mesmo tempo (cache com fonte ilegível + refetch que falhou);
   * nesse caso aparecem os dois avisos, cada um dizendo a sua verdade — o que
   * não se pode é fundir os dois numa frase só, que não seria verdade em
   * nenhum dos dois casos.
   */
  const semDadoAlgum = !data;

  /**
   * Camada de status de contrato, restaurada em 2026-08-20: a reescrita da tela
   * inicial (v2.1) a perdeu, e a tela passou a pintar linha vermelha e a somar
   * "tarefa da Conducco" para contrato já encerrado.
   *
   * DÍVIDA CONHECIDA, aceita por ora: a fonte deste status é `localStorage`
   * (marcação feita na tela de Cadastro) + a lista fixa de 20 CNPJs em
   * `contractStatus.ts:17-30`. Consequências reais: a tela mostra listas
   * diferentes em máquinas diferentes, e cliente novo nasce fora da lista —
   * cai em EM_CONFIGURACAO e sai do veredito sem ninguém ter decidido isso.
   * Mover para o backend (coluna de verdade, uma resposta para todos) é o
   * passo pendente.
   */
  const { getCnpjContractStatus } = useContractStatus();

  const { cnpjsNaCarteira, emConfiguracaoNaCarteira, apuradosNaCarteira } = useMemo(() => {
    const todos = data?.cnpjs ?? [];
    /** INATIVO sai da tela E de todo contador, veredito incluído — contrato encerrado não é assunto de hoje. */
    const naCarteira = todos.filter((cnpj) => getCnpjContractStatus(cnpj) !== "INATIVO");
    return {
      cnpjsNaCarteira: naCarteira,
      emConfiguracaoNaCarteira: naCarteira.filter((cnpj) => getCnpjContractStatus(cnpj) === "EM_CONFIGURACAO"),
      /** Base de TODA contagem por `state`: em configuração não alarma nem entra em "saudável". */
      apuradosNaCarteira: naCarteira.filter((cnpj) => getCnpjContractStatus(cnpj) !== "EM_CONFIGURACAO"),
    };
  }, [data, getCnpjContractStatus]);

  const statusOptions = useMemo(() => {
    const count = (state: IPanelHomeCnpj["state"]) => apuradosNaCarteira.filter((cnpj) => cnpj.state === state).length;
    return [
      { value: "", label: "Todos", count: cnpjsNaCarteira.length },
      { value: "CONDUCCO", label: "Exige a Conducco", count: count("CONDUCCO") },
      { value: "EM_DIA", label: "Em dia", count: count("EM_DIA") },
      { value: "PARADO", label: "Parado", count: count("PARADO") },
      /** Conta CNPJ, como as três acima — e as quatro somam exatamente "Todos". */
      { value: FILTRO_EM_CONFIGURACAO, label: "Em configuração", count: emConfiguracaoNaCarteira.length },
    ];
  }, [cnpjsNaCarteira, apuradosNaCarteira, emConfiguracaoNaCarteira]);

  const filteredCnpjs = useMemo(() => {
    return apuradosNaCarteira.filter((cnpj) => {
      if (filter.status === FILTRO_EM_CONFIGURACAO) return false;
      if (filter.status && cnpj.state !== filter.status) return false;
      return matchesPanelSearch(filter.search, cnpj);
    });
  }, [apuradosNaCarteira, filter]);

  const emConfiguracaoRows = useMemo(() => {
    if (filter.status !== "" && filter.status !== FILTRO_EM_CONFIGURACAO) return [];
    return emConfiguracaoNaCarteira.filter((cnpj) => matchesPanelSearch(filter.search, cnpj));
  }, [emConfiguracaoNaCarteira, filter]);

  // Ordem já chega correta do backend — só particiona por `state`, nunca reordena.
  const conduccoRows = filteredCnpjs.filter(
    (cnpj): cnpj is IPanelHomeCnpj & { failure: IFailureEvent } => cnpj.state === "CONDUCCO" && Boolean(cnpj.failure)
  );
  const emDiaRows = filteredCnpjs.filter((cnpj) => cnpj.state === "EM_DIA");
  const paradoRows = filteredCnpjs.filter((cnpj) => cnpj.state === "PARADO");
  /** Achado D: `state` que esta versão não conhece — nunca cai nos três setores acima (ver `isEstadoConhecido`). */
  const naoReconhecidoRows = filteredCnpjs.filter((cnpj) => !isEstadoConhecido(cnpj.state));

  /**
   * O veredito é o cabeçalho da carteira inteira, nunca a legenda da lista
   * filtrada (correção pedida pelo coordenador, rodada 1/5, 2026-08-19): um
   * filtro muda o que se olha, nunca o que é verdade sobre a carteira. Por
   * isso os totais abaixo vêm da carteira sem filtro de busca/situação, não de
   * `filteredCnpjs`/`conduccoRows` — que continuam existindo só para os
   * setores e suas linhas, que sim refletem o filtro ativo.
   *
   * A camada de contrato é a única exceção, e ela não é filtro: INATIVO não é
   * "escondido do que se olha", é fora da carteira que a tela reporta.
   */
  const conduccoTotal = apuradosNaCarteira.filter((cnpj) => cnpj.state === "CONDUCCO").length;
  /**
   * Achado D: era `cnpjsSemFiltro.length - conduccoTotal`, que jogava o `state`
   * desconhecido no mesmo saco dos saudáveis — e a frase do veredito passava a
   * afirmar algo sobre um CNPJ que a tela não sabe classificar. Agora conta só
   * os dois estados que a tela apurou; o desconhecido tem seu próprio número.
   */
  const saudavelTotal = apuradosNaCarteira.filter((cnpj) => cnpj.state === "EM_DIA" || cnpj.state === "PARADO").length;
  const naoReconhecidoTotal = apuradosNaCarteira.filter((cnpj) => !isEstadoConhecido(cnpj.state)).length;
  const emConfiguracaoTotal = emConfiguracaoNaCarteira.length;

  /** Setor "Exige a Conducco" vazio por filtro (existe falha na carteira, escondida) vs. vazio de verdade. */
  const conduccoEscondidoPeloFiltro = conduccoRows.length === 0 && conduccoTotal > 0;
  /** Mesma ambiguidade no setor "Saudáveis": filtro pode esconder tudo mesmo com `saudavelTotal > 0`. */
  const saudaveisEscondidoPeloFiltro = emDiaRows.length === 0 && paradoRows.length === 0 && saudavelTotal > 0;

  /**
   * Achado C (review final v2.1, 2026-08-20): `saudaveisEscondidoPeloFiltro`
   * exige `restTotal > 0`, então com a carteira inteira CONDUCCO (cenário real:
   * `prepare_people_data` falha em todos) o setor ficava com cabeçalho órfão —
   * título e nada embaixo, sem linha e sem mensagem, indistinguível de um bug
   * de renderização. Seção vazia agora sempre diz por que está vazia, e cada
   * motivo tem sua frase: filtro, carteira vazia, ou nada saudável mesmo.
   * `null` = tem linha pra mostrar, nenhuma mensagem.
   */
  const textoSaudaveisVazio: string | null = (() => {
    if (emDiaRows.length > 0 || paradoRows.length > 0) return null;
    if (saudaveisEscondidoPeloFiltro) return "Nenhum corresponde ao filtro atual.";
    if (cnpjsNaCarteira.length === 0) return "Nenhum CNPJ na carteira.";
    if (conduccoTotal === cnpjsNaCarteira.length) return "Nenhum CNPJ saudável agora — todos exigem a Conducco.";
    /** Sobra o `state` desconhecido e o contrato em configuração: não se pode dizer "todos exigem a Conducco". */
    return "Nenhum CNPJ saudável agora.";
  })();

  function abrirCnpj(federalTaxId: string) {
    history.push(`/v2/painel-operacional/cnpj/${federalTaxId}`);
  }

  return (
    <Page title="Rotinas">
      <Page.Tab name="Rotinas">
        <Box>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
            sx={{ mb: `${HOME_TOKENS.space.betweenRows}px` }}
          >
            {data && (
              <Typography variant="body2" color="text.secondary">
                dado de {formatSaoPauloDateTime(data.generatedAt)}
              </Typography>
            )}
            <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={() => { void refetch(); void refetchRonda(); void refetchMail(); }} disabled={isFetching}>
              atualizar
            </Button>
          </Stack>

          {isLoading ? (
            <HomeSkeleton />
          ) : semDadoAlgum ? (
            <Alert severity="warning">
              Não foi possível carregar o painel: a última tentativa falhou e não há dado anterior
              para mostrar. Use &quot;atualizar&quot; para tentar de novo.
            </Alert>
          ) : (
            <>
              {isError && (
                <Alert severity="warning" sx={{ mb: `${HOME_TOKENS.space.betweenRows}px` }}>
                  Não conseguimos confirmar o estado agora — a última atualização falhou. A lista
                  abaixo é o dado de {formatSaoPauloDateTime(data.generatedAt)} e pode estar
                  desatualizada; nunca leia como &quot;tudo certo&quot;.
                </Alert>
              )}
              {data.sourcesAvailable === false && (
                <Alert severity="warning" sx={{ mb: `${HOME_TOKENS.space.betweenRows}px` }}>
                  O servidor respondeu, mas alguma fonte dele (Airflow ou Postgres) não pôde ser
                  lida. A lista abaixo está incompleta — nunca leia como &quot;tudo certo&quot;.
                </Alert>
              )}

              <PanelFilter
                filter={filter}
                onChangeFilter={setFilter}
                onClear={() => setFilter({ search: "", status: "" })}
                statusOptions={statusOptions}
              />

              <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={conduccoTotal > 0 ? HOME_TOKENS.state.CONDUCCO.color : "text.primary"}
                >
                  {conduccoTotal} {conduccoTotal === 1 ? "tarefa" : "tarefas"} da Conducco hoje
                </Typography>
                {/*
                  Achado B (review final v2.1, 2026-08-20): era "não exigem
                  nada de ninguém agora", que inclui os PARADO — inclusive o
                  PARADO que a própria `HealthyRow` rotula "não foi possível
                  confirmar". A tela não pode afirmar ausência de demanda de
                  terceiros (nem de ninguém) sobre um CNPJ cujo estado ela
                  declara não ter confirmado. Afirma só o que apurou: não há
                  tarefa NOSSA de pé.
                */}
                <Typography variant="body2" color="text.secondary">
                  {saudavelTotal} {saudavelTotal === 1 ? "CNPJ não exige" : "CNPJs não exigem"} tarefa da Conducco.
                </Typography>
                {/* Achado D: o desconhecido é dito por escrito no veredito, não só listado lá embaixo. */}
                {naoReconhecidoTotal > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {naoReconhecidoTotal} em situação que esta tela não reconhece.
                  </Typography>
                )}
                {/*
                  Contrato em configuração tem número próprio, pelo mesmo motivo
                  do desconhecido: ele não é tarefa nossa nem é saudável, então
                  não pode se esconder dentro de nenhum dos dois números acima.
                */}
                {emConfiguracaoTotal > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {emConfiguracaoTotal} com contrato em configuração.
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
                >
                  Exige a Conducco
                </Typography>
                {conduccoRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {conduccoEscondidoPeloFiltro ? "Nenhuma falha corresponde ao filtro atual." : "Nenhuma falha de pé."}
                  </Typography>
                ) : (
                  conduccoRows.map((cnpj) => (
                    <ConduccoRow key={cnpj.federalTaxId} event={cnpj.failure} onOpen={(event) => abrirCnpj(event.federalTaxId)} />
                  ))
                )}
              </Box>

              <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
                >
                  Saudáveis
                </Typography>
                {textoSaudaveisVazio && (
                  <Typography variant="body2" color="text.secondary">
                    {textoSaudaveisVazio}
                  </Typography>
                )}
                {/*
                  `highlightSuccess` só aqui: é a única zona em que `EM_DIA`
                  significa de fato "entregou o que devia". Nas outras (Em
                  configuração, Situação não reconhecida) a mesma linha é
                  informativa, e verde ali afirmaria conquista que não houve.
                */}
                {emDiaRows.map((cnpj) => (
                  <HealthyRow key={cnpj.federalTaxId} cnpj={cnpj} onOpen={(item) => abrirCnpj(item.federalTaxId)} highlightSuccess />
                ))}
                {paradoRows.length > 0 && (
                  <Divider textAlign="left" sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                      Parados
                    </Typography>
                  </Divider>
                )}
                {paradoRows.map((cnpj) => (
                  <HealthyRow key={cnpj.federalTaxId} cnpj={cnpj} onOpen={(item) => abrirCnpj(item.federalTaxId)} />
                ))}
              </Box>

              {/*
                Zona própria pro `state` que esta versão da tela não conhece
                (Achado D, review final v2.1). Posição corrigida em 2026-08-20,
                a pedido do usuário olhando a tela: ABAIXO de "Saudáveis".
                Antes ficava em segundo lugar, logo após o alarme, por
                precaução — "não sei o que é, trate como potencialmente
                urgente". Estava errado: um `state` que esta tela não reconhece
                é, na prática, artefato de deploy não-atômico entre os dois
                repos, não cliente em apuro. Em segundo lugar ele ganhava
                primazia visual sobre clientes saudáveis REAIS, que é
                exatamente o ruído que esta tela existe pra reduzir.

                O que a mudança de posição NÃO altera: a zona continua
                existindo e separada (o risco que ela cobre é real — sem ela,
                `stateLabel` voltaria a terminar em "Em dia" e um `state` novo
                do backend viraria falso verde), continua contada à parte no
                veredito, continua fora do bucket dos saudáveis (`EM_DIA` +
                `PARADO`), continua sem cor de alarme (`error.main` é exclusivo
                do `CONDUCCO`) e sem a régua de sucesso (`highlightSuccess`
                fica no default `false`). Descer de posição não é virar
                saudável. Só renderiza quando existe linha: zona vazia aqui não
                tem o que informar (o número já aparece no veredito, que não é
                filtrado).
              */}
              {naoReconhecidoRows.length > 0 && (
                <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
                  >
                    Situação não reconhecida
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
                    O backend mandou uma situação que esta versão do painel não sabe classificar —
                    provavelmente ele subiu antes desta tela. Abra o CNPJ para ver o detalhe.
                  </Typography>
                  {naoReconhecidoRows.map((cnpj) => (
                    <HealthyRow key={cnpj.federalTaxId} cnpj={cnpj} onOpen={(item) => abrirCnpj(item.federalTaxId)} />
                  ))}
                </Box>
              )}

              {/*
                Zona do contrato em configuração — restaurada em 2026-08-20 (a
                tela antiga tinha seção separada; a reescrita perdeu a camada
                inteira).

                A tela desce por "chance de exigir tarefa NOSSA": alarme no
                topo; saudáveis (com os parados no fim da mesma zona — não
                exigem nada nosso, mas já deveriam ter entregado); a situação
                que a tela não classifica, que é quase sempre defasagem de
                deploy; e, por último entre as linhas de CNPJ, o contrato em
                configuração — o único caso em que NÃO entregar é o esperado.
                Fica acima de "Não gera tarefa" porque aquele rodapé é nota de
                DAG, não linha de CNPJ.
              */}
              {emConfiguracaoRows.length > 0 && (
                <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
                  >
                    Em configuração
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}>
                    Contrato ainda em configuração — o que aparecer aqui não gera tarefa da Conducco
                    hoje, nem entra no número do topo. Abra o CNPJ para ver o detalhe.
                  </Typography>
                  {emConfiguracaoRows.map((cnpj) => (
                    <HealthyRow key={cnpj.federalTaxId} cnpj={cnpj} onOpen={(item) => abrirCnpj(item.federalTaxId)} />
                  ))}
                </Box>
              )}

              {data && data.reliability.length > 0 && (
                <Box sx={{ mt: `${HOME_TOKENS.space.betweenZones}px` }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.6, display: "block", mb: `${HOME_TOKENS.space.withinRow}px` }}
                  >
                    Não gera tarefa
                  </Typography>
                  <Stack spacing={0.5}>
                    {data.reliability.map((note) => (
                      <ReliabilityLine key={`${note.dagName}::${note.failingSlot}`} note={note} />
                    ))}
                  </Stack>
                </Box>
              )}

            </>
          )}

          {/* hazard: fora do ternário de propósito. Dentro dele, qualquer
              falha que derrubasse a query do painel (backend fora do ar,
              token expirado, 403 de escopo) fazia a seção da Ronda não
              montar, e o aviso de "quem vigia o vigia" sumia junto com ela —
              a mesma cegueira que a Ronda existe para eliminar, na porta do
              lado. O #624 fechou só a direção interna, a de a ronda falhar
              sozinha com o painel de pé. */}
          {!isLoading && (
            <RondaSection
              status={rondaStatus}
              isError={rondaIsError}
              cnpjsNoPainel={data?.cnpjs.map((c) => c.federalTaxId) ?? []}
            />
          )}

          {!isLoading && <MailSection status={mailStatus} isError={mailIsError} />}
        </Box>
      </Page.Tab>
    </Page>
  );
}

/** Skeleton com a mesma silhueta das duas seções — nunca um spinner que apaga a tela inteira. */
function HomeSkeleton() {
  return (
    <Stack spacing={4}>
      <Skeleton variant="rounded" height={HOME_TOKENS.filter.controlHeight} width={HOME_TOKENS.filter.fieldWidth} />
      <Skeleton variant="text" width={280} height={40} />
      <Stack spacing={1}>
        {[0, 1].map((key) => (
          <Skeleton key={key} variant="rounded" height={72} />
        ))}
      </Stack>
      <Stack spacing={1}>
        {[0, 1, 2, 3].map((key) => (
          <Skeleton key={key} variant="rounded" height={HOME_TOKENS.row.minHeight} />
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * Uma linha do rodapé "Não gera tarefa" — cinza, sem alarme. O backend hoje
 * manda `reliability: []` sempre (lacuna conhecida, T-futura liga a fonte
 * por execução); quando vier populado, é isto que renderiza cada nota.
 */
function ReliabilityLine({ note }: { note: IReliabilityNote }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {note.activityName} falha às {note.failingSlot} (entrega em {note.deliveringSlots.join(", ")}) — {note.failureCount} falhas
      desde {formatSaoPauloDate(note.since)}.
    </Typography>
  );
}
